<template>
  <b-card
    class="mb-0"
  >
    <div>
      <b-row>
        <b-col
          cols="12"
          md="3"
          class="mb-2"
        >
          <h4 class="card-title mb-0 pt-50">
            Jobs</h4>
        </b-col>
        <b-col cols="3">
          <b-form-group>
            <v-select
              id="status_id"
              v-model="status_id"
              :options="status"
              :reduce="item => item.id"
              :clearable="true"
              placeholder="Select Status"
              @input="statusChanged"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="6 ml-auto"
        >
          <div class="d-flex align-items-center justify-content-end">
            <b-input-group>
              <b-form-input
                v-model="searchQuery"
                placeholder="Search"
                @keyup.enter="updateSearchQuery"
              />
              <b-input-group-append>
                <b-button
                  variant="outline-primary"
                  @click="updateSearchQuery"
                >
                  <feather-icon
                    icon="SearchIcon"
                    size="14"
                  />
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <b-button
              variant="primary"
              class="ml-2 min-w-120"
              :to="{ name: 'human-resources-jobs-new'}"
            >
              Post Job
            </b-button>
          </div>
        </b-col>
      </b-row>

    </div>

    <vue-good-table
      :columns="columns"
      :rows="jobsResponse.jobs"
      :rtl="direction"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm }"
      :pagination-options="{
        enabled: true,
        perPage:pageLength
      }"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >

        <!-- Column: Currency -->
        <span v-if="props.column.field === 'cellRendererCurrency'">
          <span>{{ props.row.bill_rate| currency('$ ', 2, { thousandsSeparator: ',' }) }}</span>
        </span>

        <!-- Column: Link -->
        <span v-if="props.column.field === 'cellRendererLink'">
          <span><router-link :to="{ name: 'job-human-resources-candidates', params: { job_id: props.row.id} }">{{ props.row.candidates }}</router-link></span>
        </span>

        <!-- Column: Button -->
        <span v-if="props.column.field === 'cellRendererApplyButton'">
          <b-button
            variant="primary"
            :to="{ name: 'job-human-resources-existing-candidates', params: { job_id: props.row.id }, query: { name: props.row.job_title } }"
            class="px-1 py-25"
          >
            Apply
          </b-button>
        </span>

        <!-- Column: Status -->
        <span v-else-if="props.column.field === 'status'">
          <b-badge :variant="statusVariant(props.row.status)">
            {{ props.row.status }}
          </b-badge>
        </span>

        <!-- Column: Action -->
        <span
          v-else-if="props.column.field === 'action'"
          class="btn-action"
        >
          <span>
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle mr-25"
                />
              </template>
              <b-dropdown-item :to="{ name: 'human-resources-jobs-clone', params: { id: props.row.id } }">
                <feather-icon icon="CopyIcon" />
                <span class="ml-50">Clone</span>
              </b-dropdown-item>
              <b-dropdown-item :to="{ name: 'human-resources-jobs-edit', params: { id: props.row.id } }">
                <feather-icon icon="Edit2Icon" />
                <span class="ml-50">Edit</span>
              </b-dropdown-item>
              <b-dropdown-item @click="confirmDeleteRecord(props.row)">
                <feather-icon icon="TrashIcon" />
                <span class="ml-50">Delete</span>
              </b-dropdown-item>
            </b-dropdown>
            <b-link
              id="view"
              :to="{ name: 'human-resources-jobs-view', params: { id: props.row.id } }"
            >
              <b-badge
                pill
                variant="light-primary"
              ><feather-icon icon="EyeIcon" />
              </b-badge>
            </b-link>
          </span>
        </span>

        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
      >
        <div class="d-flex justify-content-end flex-wrap">
          <div>
            <b-pagination
              v-model="currentPage"
              :total-rows="jobsResponse.count"
              first-number
              last-number
              align="right"
              :per-page="1"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BBadge, BPagination, BInputGroup, BFormInput, BInputGroupAppend, BDropdown, BDropdownItem, BButton, BFormGroup, BLink,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { VueGoodTable } from 'vue-good-table'
import store from '@/store/index'
import Jobs from '@/js/jobs'
import LIST from '@/js/lists'

export default {
  name: 'HrJobs',
  components: {
    BFormGroup,
    BCard,
    BRow,
    BCol,
    VueGoodTable,
    BBadge,
    BPagination,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BLink,
    BDropdown,
    BDropdownItem,
    vSelect,
  },
  data() {
    return {
      status_id: null,
      searchQuery: '',
      pageLength: 10,
      dir: false,
      jobsResponse: {
        jobs: [],
        count: 1,
      },
      status: [],
      columns: [
        {
          label: '#',
          field: 'index',
          width: '100px',
        },
        {
          label: 'Vendor',
          field: 'vendor_name',
          sortable: false,
        },
        {
          label: 'Job Title',
          field: 'job_title',
          sortable: false,
        },
        {
          label: 'Bill Rate',
          field: 'cellRendererCurrency',
          width: '150px',
          sortable: false,
        },
        {
          label: 'Candidates',
          field: 'cellRendererLink',
          width: '120px',
          sortable: false,
        },
        {
          label: 'Apply',
          field: 'cellRendererApplyButton',
          width: '100px',
          sortable: false,
        },
        {
          label: 'Action',
          field: 'action',
          width: '120px',
          sortable: false,
        },
      ],
      rows: [],
      searchTerm: '',
    }
  },
  computed: {
    currentPage: {
      get() {
        return this.$store.state.query.page
      },
      set(val) {
        this.$store.state.query.page = val
        Jobs.getJobs(this)
      },
    },
    statusVariant() {
      const statusColor = {
        /* eslint-disable key-spacing */
        Active      : 'light-success',
        Inactive    : 'light-danger',
        Terminated  : 'light-info',
        /* eslint-enable key-spacing */
      }

      return status => statusColor[status]
    },
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
  },
  watch: {
    '$store.state.lists.hrLists': {
      immediate: true,
      handler() {
        LIST.getJobStatusFullList(this)
      },
    },
  },
  created() {
    this.searchQuery = this.$store.state.query.query
    this.status_id = this.$store.state.query.status_id
    this.jobsResponse.count = this.$store.state.query.page
    Jobs.getJobs(this)
    LIST.getJobStatusFullList(this)
  },
  methods: {
    confirmDeleteRecord(obj) {
      this.$swal({
        title: 'Are you sure?',
        text: `you wan't to delete this ${obj.vendor_name}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-primary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          Jobs.delete(this, obj.id)
        }
      })
    },
    jobDeleted() {
      Jobs.getJobs(this)
      this.$swal({
        icon: 'success',
        title: 'Job Deleted!',
        text: 'Your Job has been deleted',
      })
    },
    jobNotDeleted() {
      this.$swal({
        icon: 'warning',
        title: 'Job Not Deleted!',
        text: 'Your Job has been not deleted',
      })
    },
    statusChanged() {
      this.$store.state.query.status_id = this.status_id
      this.currentPage = 1
    },
    updateSearchQuery() {
      this.$store.state.query.query = this.searchQuery
      this.currentPage = 1
    },
    setStatus(status) {
      this.status = status
    },
    setJobs(jobsResponse) {
      this.jobsResponse = jobsResponse
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
